<template>
  <div v-title="$route.meta.title"
       v-loading="loading"
       class="exam-record-detail">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: '/training/instruction' }">培训管理</el-breadcrumb-item>
        <el-breadcrumb-item>
          <a href="javascript:;" @click.prevent="goBack">答卷列表</a>
        </el-breadcrumb-item>
        <el-breadcrumb-item>答卷详情</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <training-instruction-sub-heading :title="pageHeadingTitle"
                                      :entity-id="pageEntityId"
                                      :fallback-path="fallbackListPath" />

    <el-empty v-if="loadError"
              description="答卷详情加载失败">
      <el-button type="primary"
                 @click="loadDetail">重新加载</el-button>
      <el-button @click="goBack">返回列表</el-button>
    </el-empty>

    <template v-else>
    <div class="exam-record-detail__card">
      <div class="exam-record-detail__card-header">答卷信息</div>
      <div class="exam-record-detail__card-body">
      <div class="exam-record-detail__info-grid">
        <div class="exam-record-detail__info-block">
          <h4 class="exam-record-detail__section-title">
            <i class="el-icon-user" />
            考生信息
          </h4>
          <div class="exam-record-detail__info-list">
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">姓名</span>
              <span class="exam-record-detail__info-value is-strong">{{ detail.realname || '-' }}</span>
            </div>
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">用户ID</span>
              <span class="exam-record-detail__info-value">{{ detail.userid || '-' }}</span>
            </div>
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">省市区</span>
              <span class="exam-record-detail__info-value">{{ detail.region_name || '-' }}</span>
            </div>
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">用户类型</span>
              <span class="exam-record-detail__info-value">
                <span v-if="detail.target_usertype_name"
                      :class="['exam-record-detail__usertype-tag', usertypeTagClass]">
                  {{ detail.target_usertype_name }}
                </span>
                <span v-else>-</span>
              </span>
            </div>
          </div>
        </div>
        <div class="exam-record-detail__info-block">
          <h4 class="exam-record-detail__section-title">
            <i class="el-icon-document" />
            考试信息
          </h4>
          <div class="exam-record-detail__info-list">
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">考试名称</span>
              <span class="exam-record-detail__info-value is-strong">{{ detail.training_examination_title || '-' }}</span>
            </div>
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">考试ID</span>
              <span class="exam-record-detail__info-value">{{ detail.training_examination_id || '-' }}</span>
            </div>
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">提交时间</span>
              <span class="exam-record-detail__info-value">{{ detail.examination_submitted_at || '-' }}</span>
            </div>
            <div class="exam-record-detail__info-row">
              <span class="exam-record-detail__info-label">答题用时</span>
              <span class="exam-record-detail__info-value">{{ detail.examination_duration_text || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="exam-record-detail__section-divider" />

      <div class="exam-record-detail__score-section">
        <h4 class="exam-record-detail__section-title">
          <i class="el-icon-data-analysis" />
          得分情况
        </h4>
        <div class="exam-record-detail__score-cards">
          <div :class="['exam-record-detail__score-card', statusScoreCardClass]">
            <div class="exam-record-detail__score-label">状态</div>
            <div class="exam-record-detail__score-value is-text">{{ detail.examination_status_name || '-' }}</div>
          </div>
          <div class="exam-record-detail__score-card is-total">
            <div class="exam-record-detail__score-label">总分</div>
            <div class="exam-record-detail__score-value">{{ formatOpenBookDash(detail.examination_total_score) }}</div>
          </div>
          <div class="exam-record-detail__score-card is-score">
            <div class="exam-record-detail__score-label">得分</div>
            <div class="exam-record-detail__score-value">{{ formatOpenBookDash(detail.examination_score) }}</div>
          </div>
          <div class="exam-record-detail__score-card is-passing">
            <div class="exam-record-detail__score-label">及格分</div>
            <div class="exam-record-detail__score-value">{{ formatOpenBookDash(detail.examination_passing_score) }}</div>
          </div>
          <div class="exam-record-detail__score-card is-rate">
            <div class="exam-record-detail__score-label">正确率</div>
            <div class="exam-record-detail__score-value">{{ formatOpenBookRate() }}</div>
          </div>
        </div>
      </div>

      <div class="exam-record-detail__stat-grid">
        <div class="exam-record-detail__stat-card">
          <div class="exam-record-detail__stat-label">总题数</div>
          <div class="exam-record-detail__stat-value is-blue">{{ detail.total_question_count || 0 }}</div>
        </div>
        <div class="exam-record-detail__stat-card">
          <div class="exam-record-detail__stat-label">答对题数</div>
          <div :class="['exam-record-detail__stat-value', isOpenBookExam ? '' : 'is-green']">
            {{ formatOpenBookDash(detail.correct_question_count) }}
          </div>
        </div>
        <div class="exam-record-detail__stat-card">
          <div class="exam-record-detail__stat-label">答错题数</div>
          <div :class="['exam-record-detail__stat-value', isOpenBookExam ? '' : 'is-red']">
            {{ formatOpenBookDash(detail.wrong_question_count) }}
          </div>
        </div>
      </div>
      </div>
    </div>

    <div class="exam-record-detail__card">
      <div class="exam-record-detail__card-header">答题详情{{ answerCountText }}</div>
      <div class="exam-record-detail__card-body">
      <div v-if="!(detail.answer_list || []).length"
           class="exam-record-detail__empty">
        暂无答题记录
      </div>
      <div v-else
           class="exam-record-detail__question-list">
        <div v-for="(item, index) in detail.answer_list"
             :key="item.id"
             class="exam-record-detail__question-item">
          <div class="exam-record-detail__question-head">
            <div class="exam-record-detail__question-main">
              <span class="exam-record-detail__question-index">{{ index + 1 }}</span>
              <div class="exam-record-detail__question-content">
                <div class="exam-record-detail__question-meta">
                  <span :class="['exam-record-detail__question-type-tag', getQuestionTypeTagClass(item)]">
                    {{ item.question_type_name || '题目' }}
                  </span>
                  <span class="exam-record-detail__question-score">{{ item.max_score }} 分</span>
                </div>
                <p class="exam-record-detail__question-title">{{ item.title }}</p>
                <training-media-preview :files="item.sample_files" />
              </div>
            </div>
            <div :class="['exam-record-detail__result-badge', getResultBadgeClass(item)]">
              <i v-if="shouldShowResultIcon(item)"
                 :class="item.is_correct ? 'el-icon-check' : 'el-icon-close'" />
              <span v-if="shouldShowResultScore(item)">{{ formatResultScore(item) }}</span>
            </div>
          </div>

          <div v-if="(item.options || []).length"
               class="exam-record-detail__options">
            <div v-for="option in item.options"
                 :key="option.uuid || option.key"
                 :class="['exam-record-detail__option-item', getOptionFeedback(item, option).class]">
              <span class="exam-record-detail__option-key">{{ option.key }}</span>
              <span class="exam-record-detail__option-title">{{ option.title }}</span>
              <i v-if="getOptionFeedback(item, option).mark === 'check'"
                 class="el-icon-check exam-record-detail__option-mark is-correct" />
              <i v-else-if="getOptionFeedback(item, option).mark === 'close'"
                 class="el-icon-close exam-record-detail__option-mark is-wrong" />
            </div>
          </div>

          <div class="exam-record-detail__answer-block">
            <div class="exam-record-detail__answer-row">
              <span class="exam-record-detail__answer-label">零工答案</span>
              <div :class="['exam-record-detail__answer-box', getUserAnswerBoxClass(item)]">
                {{ item.user_answer_text || '-' }}
              </div>
            </div>
            <div class="exam-record-detail__answer-row">
              <span class="exam-record-detail__answer-label">正确答案</span>
              <div class="exam-record-detail__answer-box is-standard">
                {{ item.standard_answer_text || '-' }}
              </div>
            </div>
            <div v-if="item.analysis_text || (item.analysis_files || []).length"
                 class="exam-record-detail__answer-row">
              <span class="exam-record-detail__answer-label">答案解析</span>
              <div class="exam-record-detail__answer-box is-analysis">
                <p v-if="item.analysis_text">{{ item.analysis_text }}</p>
                <training-media-preview :files="item.analysis_files" />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>

    <div class="exam-record-detail__footer">
      <el-button @click="goBack">返回答卷列表</el-button>
    </div>
    </template>
  </div>
</template>

<script>
import { consts } from '@views/training/training-instruction-record.constants'
import { consts as examConsts } from '@views/training/training-exam.constants'
import TrainingMediaPreview from '@views/training/components/TrainingMediaPreview.vue'
import TrainingInstructionSubHeading from '@views/training/components/TrainingInstructionSubHeading.vue'

export default {
  components: {
    TrainingMediaPreview,
    TrainingInstructionSubHeading,
  },
  data () {
    return {
      loading: false,
      loadError: false,
      instructionId: this.$route.params.id,
      recordId: this.$route.params.recordId,
      detail: {
        answer_list: [],
      },
    }
  },
  computed: {
    fallbackListPath () {
      return `/training/instruction/exam-records/${this.instructionId}`
    },
    pageHeadingTitle () {
      if (this.detail.realname) {
        return `答卷详情 · ${this.detail.realname}`
      }
      return '答卷详情'
    },
    pageEntityId () {
      return this.recordId || null
    },
    answerCountText () {
      const count = (this.detail.answer_list || []).length
      return count ? `（${count} 题）` : ''
    },
    usertypeTagClass () {
      const type = Number(this.detail.target_usertype)
      if (type === 2) {
        return 'is-auditor'
      }
      return 'is-worker'
    },
    isOpenBookExam () {
      return Number(this.detail.open_book_exam) === examConsts.OPEN_BOOK_EXAM_OPEN
    },
    statusScoreCardClass () {
      const status = Number(this.detail.examination_status)
      if (status === consts.EXAMINATION_STATUS_PASS) {
        return 'is-status-pass'
      }
      if (status === consts.EXAMINATION_STATUS_NOT_PASS) {
        return 'is-status-fail'
      }
      if (status === consts.EXAMINATION_STATUS_WAIT_SUBMIT) {
        return 'is-status-pending'
      }
      if (status === consts.EXAMINATION_STATUS_SUBMITTED) {
        return 'is-status-submitted'
      }
      return 'is-status-default'
    },
  },
  created () {
    this.loadDetail()
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.go(-1)
        return
      }
      this.$router.push({
        path: this.fallbackListPath,
        query: { title: this.$route.query.title || '' },
      })
    },
    loadDetail () {
      this.loading = true
      this.loadError = false
      request.common('getTrainingInstructionExamRecord', { id: this.recordId }).then(({ data }) => {
        if (Number(data.training_instruction_id) !== Number(this.instructionId)) {
          this.$message.error('答卷与当前培训不匹配')
          this.goBack()
          return
        }
        this.detail = data
      }).catch(() => {
        this.loadError = true
      }).finally(() => {
        this.loading = false
      })
    },
    formatOpenBookDash (value) {
      if (this.isOpenBookExam) {
        return '-'
      }
      if (value === null || value === undefined || value === '') {
        return 0
      }
      return value
    },
    formatOpenBookRate () {
      if (this.isOpenBookExam) {
        return '-'
      }
      return this.detail.examination_correct_rate_text || '0%'
    },
    isQuestionAnswered (item) {
      return !!(item.user_answer_text && String(item.user_answer_text).trim() && item.user_answer_text !== '-')
    },
    isAnswerGraded (item) {
      return item.is_correct === true || item.is_correct === false
    },
    getUserAnswerBoxClass (item) {
      if (!this.isQuestionAnswered(item) || !this.isAnswerGraded(item)) {
        return ''
      }
      return item.is_correct ? 'is-correct' : 'is-wrong'
    },
    getResultBadgeClass (item) {
      if (!this.isQuestionAnswered(item) || !this.isAnswerGraded(item)) {
        return 'is-neutral'
      }
      return item.is_correct ? 'is-correct' : 'is-wrong'
    },
    shouldShowResultIcon (item) {
      return this.isAnswerGraded(item)
    },
    shouldShowResultScore (item) {
      if (!this.isAnswerGraded(item)) {
        return false
      }
      if (this.isOpenBookExam && item.is_correct) {
        return false
      }
      return true
    },
    formatResultScore (item) {
      if (!this.isAnswerGraded(item)) {
        return '-'
      }
      if (item.is_correct) {
        return `+${item.score || 0}分`
      }
      return '0分'
    },
    parseAnswerKeys (text) {
      if (!text || text === '-') {
        return []
      }
      return String(text).split('、').map(key => key.trim()).filter(Boolean)
    },
    getOptionFeedback (item, option) {
      const questionType = item.question_type
      if (questionType !== 'single_select' && questionType !== 'multi_select') {
        return { class: '', mark: '' }
      }

      if (!this.isQuestionAnswered(item) || !this.isAnswerGraded(item)) {
        return { class: '', mark: '' }
      }

      const userKeys = this.parseAnswerKeys(item.user_answer_text)
      const standardKeys = this.parseAnswerKeys(item.standard_answer_text)
      const isUser = userKeys.includes(option.key)
      const isStandard = standardKeys.includes(option.key)

      if (isUser && isStandard) {
        return { class: 'is-user-correct', mark: 'check' }
      }
      if (isUser && !isStandard) {
        return { class: 'is-user-wrong', mark: 'close' }
      }
      return { class: '', mark: '' }
    },
    getQuestionTypeTagClass (item) {
      const map = {
        single_select: 'is-single',
        multi_select: 'is-multi',
        fill_blank: 'is-fill',
      }
      return map[item.question_type] || 'is-default'
    },
  },
}
</script>

<style lang="scss" scoped>
.exam-record-detail {
  &__card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;

    & + & {
      margin-top: 16px;
    }
  }

  &__card-header {
    padding: 12px 24px;
    border-bottom: 1px solid #ebeef5;
    color: #606266;
    font-size: 14px;
    font-weight: 500;
  }

  &__card-body {
    padding: 20px 24px;
  }

  &__info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
  }

  &__info-block {
    padding: 16px 18px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    background: #fafbfc;
  }

  &__section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 14px;
    font-size: 15px;
    font-weight: 600;
    color: #303133;

    i {
      color: #409eff;
      font-size: 16px;
    }
  }

  &__info-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 22px;
  }

  &__info-label {
    width: 72px;
    flex-shrink: 0;
    color: #909399;
    font-size: 14px;
    line-height: 22px;
  }

  &__info-value {
    flex: 1;
    min-width: 0;
    color: #303133;
    font-size: 14px;
    line-height: 22px;
    word-break: break-all;

    &.is-strong {
      font-weight: 600;
    }
  }

  &__usertype-tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;

    &.is-worker {
      background: #dbeafe;
      color: #1d4ed8;
    }

    &.is-auditor {
      background: #ede9fe;
      color: #6d28d9;
    }
  }

  &__section-divider {
    height: 1px;
    margin: 20px 0;
    background: #ebeef5;
  }

  &__score-section {
    margin-bottom: 16px;
  }

  &__score-cards {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 12px;
  }

  &__score-card {
    border-radius: 8px;
    padding: 14px 16px;
    min-height: 78px;

    &.is-total {
      background: linear-gradient(135deg, #eff6ff, #dbeafe);
      color: #1d4ed8;
    }

    &.is-score {
      background: linear-gradient(135deg, #ecfdf5, #d1fae5);
      color: #047857;
    }

    &.is-passing {
      background: linear-gradient(135deg, #fff7ed, #ffedd5);
      color: #c2410c;
    }

    &.is-rate {
      background: linear-gradient(135deg, #f5f3ff, #ede9fe);
      color: #6d28d9;
    }

    &.is-status-pass {
      background: linear-gradient(135deg, #ecfdf5, #d1fae5);
      color: #047857;
    }

    &.is-status-fail {
      background: linear-gradient(135deg, #fef2f2, #fee2e2);
      color: #dc2626;
    }

    &.is-status-pending {
      background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
      color: #4b5563;
    }

    &.is-status-submitted {
      background: linear-gradient(135deg, #e0f2fe, #bae6fd);
      color: #0369a1;
    }

    &.is-status-default {
      background: linear-gradient(135deg, #f8fafc, #f1f5f9);
      color: #64748b;
    }
  }

  &__score-label {
    font-size: 13px;
    margin-bottom: 8px;
    opacity: 0.9;
  }

  &__score-value {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;

    &.is-text {
      font-size: 18px;
      line-height: 1.35;
    }
  }

  &__stat-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  &__stat-card {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    padding: 14px 16px;
    background: #fff;
  }

  &__stat-label {
    color: #909399;
    font-size: 13px;
    margin-bottom: 8px;
  }

  &__stat-value {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;

    &.is-blue {
      color: #409eff;
    }

    &.is-green {
      color: #67c23a;
    }

    &.is-red {
      color: #f56c6c;
    }
  }

  &__empty {
    padding: 32px 0;
    text-align: center;
    color: #909399;
    font-size: 14px;
  }

  &__question-list {
    margin: -4px 0 0;
  }

  &__question-item {
    padding: 20px 0;
    border-bottom: 1px solid #ebeef5;

    &:first-child {
      padding-top: 4px;
    }

    &:last-child {
      border-bottom: none;
      padding-bottom: 4px;
    }
  }

  &__question-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 14px;
  }

  &__question-main {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  &__question-index {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f0f2f5;
    color: #606266;
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__question-content {
    flex: 1;
    min-width: 0;
  }

  &__question-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__question-type-tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;

    &.is-single {
      background: #dbeafe;
      color: #1d4ed8;
    }

    &.is-multi {
      background: #ede9fe;
      color: #6d28d9;
    }

    &.is-fill {
      background: #dcfce7;
      color: #15803d;
    }

    &.is-default {
      background: #f3f4f6;
      color: #4b5563;
    }
  }

  &__question-score {
    color: #909399;
    font-size: 13px;
  }

  &__question-title {
    margin: 0;
    color: #303133;
    font-size: 15px;
    line-height: 1.65;
  }

  &__result-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
    white-space: nowrap;

    &.is-correct {
      background: #ecfdf5;
      color: #047857;
    }

    &.is-wrong {
      background: #fef2f2;
      color: #dc2626;
    }

    &.is-neutral {
      background: #f5f7fa;
      color: #909399;
    }
  }

  &__options {
    margin: 0 0 14px 44px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__option-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f5f7fa;
    border: 1px solid transparent;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &.is-user-correct {
      background: #ecfdf5;
      border-color: #bbf7d0;
    }

    &.is-user-wrong {
      background: #fef2f2;
      border-color: #fecaca;
    }
  }

  &__option-key {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid #dcdfe6;
    background: #fff;
    color: #606266;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__option-title {
    flex: 1;
    min-width: 0;
    color: #606266;
    font-size: 14px;
    line-height: 24px;
  }

  &__option-mark {
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;

    &.is-correct {
      color: #047857;
    }

    &.is-wrong {
      color: #dc2626;
    }
  }

  &__answer-block {
    margin-left: 44px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__answer-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  &__answer-label {
    width: 72px;
    flex-shrink: 0;
    padding-top: 8px;
    color: #909399;
    font-size: 14px;
    line-height: 22px;
  }

  &__answer-box {
    flex: 1;
    min-width: 0;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.65;
    word-break: break-word;

    &:not(.is-correct):not(.is-wrong):not(.is-standard):not(.is-analysis) {
      background: #f5f7fa;
      border: 1px solid #ebeef5;
      color: #606266;
    }

    &.is-correct {
      background: #ecfdf5;
      border: 1px solid #bbf7d0;
      color: #047857;
      font-weight: 500;
    }

    &.is-wrong {
      background: #fef2f2;
      border: 1px solid #fecaca;
      color: #dc2626;
      font-weight: 500;
    }

    &.is-standard {
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      color: #1d4ed8;
      font-weight: 500;
    }

    &.is-analysis {
      background: #fffbeb;
      border: 1px solid #fde68a;
      color: #92400e;

      p {
        margin: 0;
      }
    }
  }

  &__footer {
    margin: 16px 0 8px;
    text-align: center;
  }
}

@media (max-width: 1200px) {
  .exam-record-detail {
    &__score-cards {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}

@media (max-width: 992px) {
  .exam-record-detail {
    &__info-grid,
    &__stat-grid {
      grid-template-columns: 1fr;
    }

    &__score-cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@media (max-width: 768px) {
  .exam-record-detail {
    &__question-head {
      flex-direction: column;
    }

    &__result-badge {
      margin-left: 44px;
    }

    &__options,
    &__answer-block {
      margin-left: 0;
    }

    &__score-cards {
      grid-template-columns: 1fr;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/instruction/TrainingInstructionExamRecordDetail.vue